import React from "react";
import { Formik } from "formik";
import { CForm, CRow, CCol, CButton, CSpinner } from "@coreui/react";
import CustomInput from "@/components/CustomInput";
import { subscriptionPlan } from "@/constants/translation/subscriptionPlan.translation";
import { useTranslationContext } from "@/context/TranslationContext";
import { useNavigate } from "react-router-dom";
import { masters } from "@/libs/routes";
import constants from "@/libs/constant";

/**
 * SubscriptionPlanForm Component
 * @param {Object} props - Component props
 * @param {Object} props.initialValues - Initial form values ({ name, user_type, duration, apple_product_id, google_product_id })
 * @param {Function} props.onSubmit - Function to handle form submission
 * @param {boolean} props.isSubmitting - Loading state for the submit button
 * @param {Object} [props.validationSchema] - Form validation schema
 * @param {boolean} [props.isEdit] - Whether the form is used for editing
 * @returns {JSX.Element} React component for the subscription plan add/edit form
 */
const SubscriptionPlanForm = ({ initialValues, onSubmit, isSubmitting, validationSchema, isEdit = false }) => {
  const t = useTranslationContext();
  const navigate = useNavigate();

  const userTypeOptions = [
    { value: constants.userType.normalUser, label: t(subscriptionPlan.form.user_type_options.normal_user) },
    { value: constants.userType.freelancerUser, label: t(subscriptionPlan.form.user_type_options.freelancer_user) },
  ];

  const durationOptions = [
    { value: constants.subscriptionPlanDuration.weekly, label: t(subscriptionPlan.form.duration_options.weekly) },
    { value: constants.subscriptionPlanDuration.monthly, label: t(subscriptionPlan.form.duration_options.monthly) },
    {
      value: constants.subscriptionPlanDuration.quarterly,
      label: t(subscriptionPlan.form.duration_options.quarterly),
    },
    {
      value: constants.subscriptionPlanDuration.semiannually,
      label: t(subscriptionPlan.form.duration_options.semiannually),
    },
    { value: constants.subscriptionPlanDuration.annually, label: t(subscriptionPlan.form.duration_options.annually) },
  ];

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values, { setSubmitting }) => {
        onSubmit(values);
        setSubmitting(false);
      }}
      enableReinitialize
      validationSchema={validationSchema}
    >
      {({ values, handleSubmit, handleChange, handleBlur, setFieldValue, errors, touched }) => (
        <CForm
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit(e);
          }}
        >
          <CRow className="mb-2 m-auto p-3 border rounded">
            <CCol lg={6} md={6} sm={12}>
              <CustomInput
                label={t(subscriptionPlan.form.name_label)}
                type={constants.inputTypes.text}
                name="name"
                placeholder={t(subscriptionPlan.form.name_placeholder)}
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.name && errors.name}
              />
            </CCol>
            <CCol lg={6} md={6} sm={12}>
              <CustomInput
                label={t(subscriptionPlan.form.user_type_label)}
                type={constants.inputTypes.select}
                name="user_type"
                placeholder={t(subscriptionPlan.form.user_type_placeholder)}
                options={userTypeOptions}
                value={userTypeOptions.find((option) => option.value === values.user_type) || null}
                onChange={(option) => setFieldValue("user_type", option?.value || "")}
                onBlur={handleBlur}
                error={touched.user_type && errors.user_type}
                // Scoped dark-theme override (this form's User Type field only) - see
                // .subscription-user-type-select in _customInput.scss. Deliberately NOT the
                // shared default "react-select-prefix", so every other select-type CustomInput
                // elsewhere in the admin panel keeps its current light styling untouched.
                classNamePrefix="subscription-user-type-select"
              />
            </CCol>
            <CCol lg={6} md={6} sm={12}>
              <CustomInput
                label={t(subscriptionPlan.form.duration_label)}
                type={constants.inputTypes.select}
                name="duration"
                placeholder={t(subscriptionPlan.form.duration_placeholder)}
                options={durationOptions}
                value={durationOptions.find((option) => option.value === values.duration) || null}
                onChange={(option) => setFieldValue("duration", option?.value || "")}
                onBlur={handleBlur}
                error={touched.duration && errors.duration}
                classNamePrefix="subscription-user-type-select"
              />
            </CCol>
            <CCol lg={6} md={6} sm={12}>
              <CustomInput
                label={t(subscriptionPlan.form.apple_product_id_label)}
                type={constants.inputTypes.text}
                name="apple_product_id"
                placeholder={t(subscriptionPlan.form.apple_product_id_placeholder)}
                value={values.apple_product_id}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.apple_product_id && errors.apple_product_id}
              />
            </CCol>
            <CCol lg={6} md={6} sm={12}>
              <CustomInput
                label={t(subscriptionPlan.form.google_product_id_label)}
                type={constants.inputTypes.text}
                name="google_product_id"
                placeholder={t(subscriptionPlan.form.google_product_id_placeholder)}
                value={values.google_product_id}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.google_product_id && errors.google_product_id}
              />
            </CCol>
          </CRow>

          <CRow className="m-auto p-3">
            <CCol className="d-flex gap-2 justify-content-center">
              <CButton type="submit" color="primary" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <CSpinner size="sm" className="me-2" />
                    {t(subscriptionPlan.form.loading_btn_text)}
                  </>
                ) : isEdit ? (
                  t(subscriptionPlan.form.update_btn_text)
                ) : (
                  t(subscriptionPlan.form.submit_btn_text)
                )}
              </CButton>
              <CButton
                type="button"
                color="secondary"
                disabled={isSubmitting}
                onClick={() => navigate(masters.subscriptionPlan.list)}
              >
                {t(subscriptionPlan.form.cancel_btn_text)}
              </CButton>
            </CCol>
          </CRow>
        </CForm>
      )}
    </Formik>
  );
};

export default SubscriptionPlanForm;
